import * as React from 'react';
import { Footer } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Checkbox from 'antd/lib/checkbox';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import Tooltip from 'antd/lib/tooltip';
import { RocketOutlined, SearchOutlined } from '@ant-design/icons';

import { StepRun } from '@/types/stepRun';
import {
    AmazonAppStoreData,
    AppleAppStoreData,
    AppPlatform,
    GooglePlayStoreData,
    HuaweiAppStoreData,
    ReleaseStepStatus,
    ReleaseStepSubmissionData,
    RunwayResponseType,
    SamsungAppStoreData,
    TransactionType,
} from '@/types';
import styles from '../../styles/stepStyles';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import { StepStatusAlert } from '../../StepStatusHeaderComponent';
import WorkflowsPlatformIcon from '../../icons/WorkflowsPlatformIcon';
import { getStepRunSummaryPanelByArchetype } from '../archetypeSpecificStepRunSummaryPanels';
import { globalMessage } from '@/utils/globalMessage';
import { TransactionStatus } from '@/types/batchTransaction';
import { WorkflowRunUpdatable } from '@/actions/workflowRun';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { useSelector } from 'react-redux';
import { selectWorkflowId, selectWorkflowRunId } from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { appleAppCanBeDeveloperRejected, getIsAppleAppStore, isAppReleased, isAppSubmitted } from '@/helpers';

export interface BatchTransactionBarProps {
    orgId: string;
    appId: string;
    releaseId: string;
    stepRuns: StepRun[];
    transactionTypes: TransactionType[];
}

export const actionForTransactionType = (transactionType: TransactionType): string => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return 'Confirm and submit for review...';
        case TransactionType.ReleaseVersion:
            return 'Confirm and release version...';
        case TransactionType.RejectAppStoreRelease:
            return 'Confirm and pull from review...';
    }
};

export const modalTitleForTransactionType = (transactionType: TransactionType): string => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return 'Confirm and submit update for review';
        case TransactionType.ReleaseVersion:
            return 'Confirm and release version';
        case TransactionType.RejectAppStoreRelease:
            return 'Confirm and pull from review';
    }
};

export const modalDescriptionForTransactionType = (transactionType: TransactionType): string => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return 'Selected step configs below will be submitted to their respective app stores for review.';
        case TransactionType.ReleaseVersion:
            return 'Selected step configs below will be released to their respective app stores.';
        case TransactionType.RejectAppStoreRelease:
            return 'Selected step configs below will be pulled from review.';
    }
};

export const confirmButtonTextForTransactionType = (
    transactionType: TransactionType,
    selectedCount: number,
): string => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return `Confirm and submit update for ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.ReleaseVersion:
            return `Confirm and release ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.RejectAppStoreRelease:
            return `Pull ${selectedCount} step config${selectedCount !== 1 ? 's' : ''} from review`;
    }
};

export const iconForTransactionType = (transactionType: TransactionType): React.ReactNode => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
        case TransactionType.RejectAppStoreRelease:
            return <SearchOutlined />;
        case TransactionType.ReleaseVersion:
            return <RocketOutlined />;
    }
};

export const isStepRunSelectableForTransactionOrError = (
    transactionType: TransactionType,
    stepRun: StepRun,
): string | null => {
    const currentlyRunningStepTransaction = stepRun.stepTransactions.find(
        (st) =>
            st.transactionType === transactionType &&
            (st.status === TransactionStatus.Requested || st.status === TransactionStatus.PendingRetry),
    );

    if (currentlyRunningStepTransaction) {
        return `This step is currently running this transaction already. Please wait for it to complete before running another one.`;
    }

    switch (transactionType) {
        case TransactionType.SubmitVersion: {
            if (stepRun.status === ReleaseStepStatus.Ready) {
                return 'This step is ready and cannot be submitted for review';
            }
            if (stepRun.status === ReleaseStepStatus.Blocked) {
                return 'This step is blocked and cannot be submitted for review';
            }
            return null;
        }
        case TransactionType.ReleaseVersion: {
            if (stepRun.status === ReleaseStepStatus.Ready) {
                return 'This step is ready and cannot be released';
            }
            if (stepRun.status === ReleaseStepStatus.Blocked) {
                return 'This step is blocked and cannot be released';
            }
            return null;
        }
        case TransactionType.RejectAppStoreRelease: {
            const stepData: ReleaseStepSubmissionData = {
                ...stepRun.data.summaryData,
                ...stepRun.data.complementaryData,
            };

            const isAppleAppStore = getIsAppleAppStore(stepData.appStoreType);
            if (!isAppleAppStore) {
                return 'The destination for this step is not an Apple app store, so pulling from review is unsupported';
            }

            const appStoreData = stepData.appStoreData as AppleAppStoreData;
            const googleStoreData = stepData.appStoreData as GooglePlayStoreData;
            const amazonData = stepData.appStoreData as AmazonAppStoreData;
            const huaweiData = stepData.appStoreData as HuaweiAppStoreData;
            const samsungData = stepData.appStoreData as SamsungAppStoreData;

            const isSubmitted = isAppSubmitted(
                stepData.appStoreType,
                appStoreData?.versionReviewSubmissionItem?.state,
                appStoreData?.relevantReviewSubmission?.state,
                googleStoreData?.state,
                amazonData?.state,
                huaweiData?.state,
                samsungData?.state,
            );
            const isReleased = isAppReleased(
                stepData.appStoreType,
                appStoreData?.versionReviewSubmissionItem?.state,
                googleStoreData?.state,
                amazonData?.state,
                huaweiData?.state,
                samsungData?.state,
            );

            const appCanBeDeveloperRejected =
                appStoreData ? appleAppCanBeDeveloperRejected(appStoreData?.versionReviewSubmissionItem?.state) : false;

            if (!(appCanBeDeveloperRejected && isSubmitted && !isReleased)) {
                if (isSubmitted && isReleased) {
                    return 'This step has already been fully released to its destination store, and can no longer be pulled from review';
                }
                if (!isSubmitted) {
                    return 'This step has not been submitted for review yet';
                }

                return 'This step is not in a valid state to be pulled from review';
            }

            return null;
        }
        default:
            return 'Unknown transaction type';
    }
};

export interface SubmitVersionFormValues {
    selectedStepRunIds: string[];
    releasePreviousSubmissionToAllUsersFirst: boolean;
}

export interface ReleaseVersionFormValues {
    selectedStepRunIds: string[];
}

export type BatchTransactionFormValues = SubmitVersionFormValues | ReleaseVersionFormValues;

export const extraOptionsForTransactionType = (
    transactionType: TransactionType,
    stepRuns: StepRun[],
): React.ReactNode => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return (
                <div style={{ marginBottom: 16 }}>
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                            const selectedStepRunIds = getFieldValue('selectedStepRunIds');
                            const selectedStepRuns = stepRuns.filter((sr) => selectedStepRunIds.includes(sr.id));
                            const someSelectedStepCannotBeIncremented = selectedStepRuns.some((sr) =>
                                sr.destinations.some((d) => d.platform !== AppPlatform.Android),
                            );

                            return (
                                <Form.Item
                                    name="releasePreviousSubmissionToAllUsersFirst"
                                    valuePropName="checked"
                                    initialValue={false}
                                >
                                    <Checkbox disabled={someSelectedStepCannotBeIncremented}>
                                        Increment prior release to 100% before next release.
                                    </Checkbox>
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </div>
            );
        case TransactionType.ReleaseVersion:
            return (
                <div style={{ marginBottom: 16 }}>
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                            const selectedStepRunIds = getFieldValue('selectedStepRunIds');
                            const selectedStepRuns = stepRuns.filter((sr) => selectedStepRunIds.includes(sr.id));
                            const someSelectedStepCannotBeIncremented = selectedStepRuns.some((sr) =>
                                sr.destinations.some((d) => d.platform !== AppPlatform.Ios),
                            );

                            return (
                                <Form.Item
                                    name="releasePreviousSubmissionToAllUsersFirst"
                                    valuePropName="checked"
                                    initialValue={false}
                                >
                                    <Checkbox disabled={someSelectedStepCannotBeIncremented}>
                                        Increment prior release to 100% before next release.
                                    </Checkbox>
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </div>
            );
        case TransactionType.RejectAppStoreRelease:
            return null;
    }
};

interface ConfirmationStepRunPanelProps {
    stepRun: StepRun;
    appId: string;
    isSelected: boolean;
    isDisabled: boolean;
    onSelect: () => void;
    errorMessage: string | null;
}

const ConfirmationStepRunPanel: React.FC<ConfirmationStepRunPanelProps> = ({
    stepRun,
    appId,
    isSelected,
    isDisabled,
    onSelect,
    errorMessage,
}) => {
    const stepName = stepRun.displayName || stepRun.stepId;
    const archetypeSpecificPanel = getStepRunSummaryPanelByArchetype({
        stepRun,
        appId,
        shouldDisplayCompactVersion: true,
    });

    const panelContent = (
        <div
            style={{
                borderRadius: 8,
                border: `1px solid ${isSelected ? runwayColors.info : antGrey.grey5}`,
                backgroundColor: runwayColors.white,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) {
                    onSelect();
                }
            }}
        >
            <Space style={{ marginBottom: 12 }} align="center">
                {stepRun.destinations.map((destination, index) => (
                    <WorkflowsPlatformIcon key={index} platform={destination.platform} width={24} height={24} />
                ))}
                <Text
                    strong
                    style={{
                        fontSize: 16,
                        color: antGrey.grey8,
                    }}
                >
                    {stepName}
                </Text>
            </Space>

            <StepStatusAlert
                status={stepRun.status}
                statusReasonString={stepRun.statusReasonString}
                style={{ marginBottom: 16 }}
            />

            {archetypeSpecificPanel && <div>{archetypeSpecificPanel}</div>}
        </div>
    );

    if (errorMessage) {
        return <Tooltip title={errorMessage}>{panelContent}</Tooltip>;
    }

    return panelContent;
};

interface ConfirmationStepStatusSectionProps {
    title: string;
    steps: StepRun[];
    color: string;
    appId: string;
    transactionType: TransactionType;
    selectedStepRunIds: Set<string>;
    onStepSelectionChange: (stepRunId: string, checked: boolean) => void;
    onSectionSelectionChange: (stepRunIds: string[], checked: boolean) => void;
}

const ConfirmationStepStatusSection: React.FC<ConfirmationStepStatusSectionProps> = ({
    title,
    steps,
    color,
    appId,
    transactionType,
    selectedStepRunIds,
    onStepSelectionChange,
    onSectionSelectionChange,
}) => {
    const selectableSteps = steps.filter(
        (step) => isStepRunSelectableForTransactionOrError(transactionType, step) === null,
    );
    const allSelected = selectableSteps.every((step) => selectedStepRunIds.has(step.id));
    const someSelected = selectableSteps.some((step) => selectedStepRunIds.has(step.id));
    const selectableStepIds = selectableSteps.map((step) => step.id);

    return (
        <div style={{ marginBottom: 16 }}>
            <Flex align="center" gap={12} style={{ marginBottom: 8 }}>
                <Checkbox
                    checked={selectableSteps.length > 0 && allSelected}
                    indeterminate={someSelected && !allSelected}
                    disabled={selectableSteps.length === 0}
                    onChange={(e) => onSectionSelectionChange(selectableStepIds, e.target.checked)}
                >
                    <Text strong style={{ fontSize: 14, color: color }}>
                        {title}
                    </Text>
                </Checkbox>
            </Flex>
            <Divider style={{ margin: '8px 0' }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {steps.map((step) => {
                    const errorMessage = isStepRunSelectableForTransactionOrError(transactionType, step);
                    const isSelectable = errorMessage === null;
                    return (
                        <Flex key={step.id} align="flex-start" gap={12}>
                            <Checkbox
                                checked={selectedStepRunIds.has(step.id)}
                                disabled={!isSelectable}
                                onChange={(e) => onStepSelectionChange(step.id, e.target.checked)}
                                style={{ marginTop: 12 }}
                            />
                            <div style={{ flex: 1 }}>
                                <ConfirmationStepRunPanel
                                    stepRun={step}
                                    appId={appId}
                                    isSelected={selectedStepRunIds.has(step.id)}
                                    isDisabled={!isSelectable}
                                    onSelect={() => {
                                        if (isSelectable) {
                                            onStepSelectionChange(step.id, !selectedStepRunIds.has(step.id));
                                        }
                                    }}
                                    errorMessage={errorMessage}
                                />
                            </div>
                        </Flex>
                    );
                })}
            </div>
        </div>
    );
};

interface BatchTransactionModalProps {
    visible: boolean;
    onClose: () => void;
    transactionType: TransactionType;
    stepRuns: StepRun[];
    appId: string;
    orgId: string;
    releaseId: string;
}

const BatchTransactionModal: React.FC<BatchTransactionModalProps> = ({
    visible,
    onClose,
    transactionType,
    stepRuns,
    appId,
    orgId,
    releaseId,
}) => {
    const [form] = Form.useForm<BatchTransactionFormValues>();
    const [isFormValid, setIsFormValid] = React.useState(false);

    const selectedStepRunIds = Form.useWatch('selectedStepRunIds', form) || [];
    const selectedStepRunIdSet = new Set(selectedStepRunIds);

    const pendingSteps = stepRuns.filter(
        (sr) => sr.status === ReleaseStepStatus.Pending || sr.status === ReleaseStepStatus.PendingChecklistOnly,
    );
    const readySteps = stepRuns.filter((sr) => sr.status === ReleaseStepStatus.Ready);
    const blockedSteps = stepRuns.filter((sr) => sr.status === ReleaseStepStatus.Blocked);
    const inactiveSteps = stepRuns.filter((sr) => sr.status === ReleaseStepStatus.Inactive);

    const selectableStepRuns = stepRuns.filter(
        (sr) => isStepRunSelectableForTransactionOrError(transactionType, sr) === null,
    );
    const selectableStepIds = selectableStepRuns.map((sr) => sr.id);
    const allSelectedSelected = selectableStepIds.every((id) => selectedStepRunIdSet.has(id));
    const someSelectedSelected = selectableStepIds.some((id) => selectedStepRunIdSet.has(id));

    const handleStepSelectionChange = (stepRunId: string, checked: boolean) => {
        const currentIds = form.getFieldValue('selectedStepRunIds') || [];
        const newIds = checked ? [...currentIds, stepRunId] : currentIds.filter((id: string) => id !== stepRunId);
        form.setFieldsValue({ selectedStepRunIds: newIds } as BatchTransactionFormValues);
        handleFormValuesChange();
    };

    const handleSectionSelectionChange = (stepRunIds: string[], checked: boolean) => {
        const currentIds = form.getFieldValue('selectedStepRunIds') || [];
        const currentSet = new Set(currentIds);

        stepRunIds.forEach((id) => {
            if (checked) {
                currentSet.add(id);
            } else {
                currentSet.delete(id);
            }
        });

        form.setFieldsValue({ selectedStepRunIds: Array.from(currentSet) } as BatchTransactionFormValues);
        handleFormValuesChange();
    };

    const handleSelectAll = (checked: boolean) => {
        const newIds = checked ? selectableStepIds : [];
        form.setFieldsValue({ selectedStepRunIds: newIds } as BatchTransactionFormValues);
        handleFormValuesChange();
    };

    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));

    const [triggerBatchAction, isTriggerBatchActionLoading, triggerBatchActionResponseType, triggerBatchActionError] =
        useCallUpdatable(
            WorkflowRunUpdatable.TriggerBatchAction,
            {
                orgId,
                workflowId,
                workflowRunId,
            },
            stepRuns.map((sr) => sr.id).join(','),
        );

    React.useEffect(() => {
        if (triggerBatchActionResponseType === RunwayResponseType.Success) {
            onClose();
        }
    }, [triggerBatchActionResponseType]);

    React.useEffect(() => {
        if (triggerBatchActionError) {
            globalMessage.error(triggerBatchActionError);
        }
    }, [triggerBatchActionError]);

    const handleConfirm = async () => {
        try {
            const { selectedStepRunIds, ...rest } = await form.validateFields();
            const stepArchetype = stepRuns?.[0]?.type;
            triggerBatchAction({
                targetStepRunIds: selectedStepRunIds,
                transactionType,
                stepArchetype,
                customRequestData: rest,
            });
        } catch (error) {
            globalMessage.error(error.message);
        }
    };

    const handleFormValuesChange = () => {
        const selectedIds = form.getFieldValue('selectedStepRunIds') || [];
        const hasSelectedSteps = selectedIds.length > 0;

        form.validateFields({ validateOnly: true })
            .then(() => setIsFormValid(hasSelectedSteps))
            .catch(() => setIsFormValid(false));
    };

    return (
        <Modal
            title={modalTitleForTransactionType(transactionType)}
            open={visible}
            onCancel={onClose}
            okButtonProps={{ loading: isTriggerBatchActionLoading, disabled: !isFormValid }}
            okText={confirmButtonTextForTransactionType(transactionType, selectedStepRunIds.length)}
            onOk={handleConfirm}
            cancelText="Cancel"
            destroyOnClose
            width={800}
        >
            <Text style={{ color: antGrey.grey7, display: 'block', marginBottom: 24 }}>
                {modalDescriptionForTransactionType(transactionType)}
            </Text>

            <Form form={form} layout="vertical" onValuesChange={handleFormValuesChange}>
                <Form.Item
                    name="selectedStepRunIds"
                    initialValue={[]}
                    preserve={false}
                    rules={[
                        {
                            validator: (_, value) => {
                                if (!value || value.length === 0) {
                                    return Promise.reject(new Error('Please select at least one step'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                    hidden
                >
                    <input type="hidden" />
                </Form.Item>
                {extraOptionsForTransactionType(transactionType, stepRuns)}
            </Form>

            <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
                <Checkbox
                    checked={allSelectedSelected}
                    indeterminate={someSelectedSelected && !allSelectedSelected}
                    disabled={selectableStepIds.length === 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                >
                    <Text strong>Select all</Text>
                </Checkbox>
            </Flex>
            <Divider style={{ margin: '16px 0' }} />

            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                {pendingSteps.length > 0 && (
                    <ConfirmationStepStatusSection
                        title="Waiting"
                        steps={pendingSteps}
                        color={runwayColors.statusYellow}
                        appId={appId}
                        transactionType={transactionType}
                        selectedStepRunIds={selectedStepRunIdSet}
                        onStepSelectionChange={handleStepSelectionChange}
                        onSectionSelectionChange={handleSectionSelectionChange}
                    />
                )}
                {readySteps.length > 0 && (
                    <ConfirmationStepStatusSection
                        title="Ready"
                        steps={readySteps}
                        color={runwayColors.statusGreen}
                        appId={appId}
                        transactionType={transactionType}
                        selectedStepRunIds={selectedStepRunIdSet}
                        onStepSelectionChange={handleStepSelectionChange}
                        onSectionSelectionChange={handleSectionSelectionChange}
                    />
                )}
                {blockedSteps.length > 0 && (
                    <ConfirmationStepStatusSection
                        title="Blocked"
                        steps={blockedSteps}
                        color={runwayColors.statusRed}
                        appId={appId}
                        transactionType={transactionType}
                        selectedStepRunIds={selectedStepRunIdSet}
                        onStepSelectionChange={handleStepSelectionChange}
                        onSectionSelectionChange={handleSectionSelectionChange}
                    />
                )}
                {inactiveSteps.length > 0 && (
                    <ConfirmationStepStatusSection
                        title="Inactive"
                        steps={inactiveSteps}
                        color={antGrey.grey6}
                        appId={appId}
                        transactionType={transactionType}
                        selectedStepRunIds={selectedStepRunIdSet}
                        onStepSelectionChange={handleStepSelectionChange}
                        onSectionSelectionChange={handleSectionSelectionChange}
                    />
                )}
            </div>
        </Modal>
    );
};

export const BatchTransactionBar: React.FC<BatchTransactionBarProps> = ({
    orgId,
    appId,
    releaseId,
    stepRuns,
    transactionTypes,
}) => {
    const [activeModal, setActiveModal] = React.useState<TransactionType | null>(null);

    if (transactionTypes.length === 0) {
        return null;
    }

    return (
        <>
            <Footer style={styles.footerStyle}>
                <Space>
                    {transactionTypes.map((transactionType) => (
                        <Button
                            key={transactionType}
                            icon={iconForTransactionType(transactionType)}
                            type="primary"
                            onClick={() => setActiveModal(transactionType)}
                        >
                            {actionForTransactionType(transactionType)}
                        </Button>
                    ))}
                </Space>
            </Footer>

            <BatchTransactionModal
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                visible={activeModal !== null}
                onClose={() => setActiveModal(null)}
                transactionType={activeModal}
                stepRuns={stepRuns}
            />
        </>
    );
};
